export class Template {
    id: number;
    name: string;
    description: string;
    contentType: string;
    transitionType: string;
    orientation: string;
    html: string;
    updatedAt: number;
    archivedAt: number | null;
    uid: number | null;
    businessName: string | null;

    constructor(
        id: number,
        name: string,
        description: string,
        contentType: string,
        transitionType: string,
        orientation: string,
        html: string,
        updatedAt: number,
        archivedAt: number | null = null,
        uid: number | null = null,
        businessName: string | null = null,
    ) {
        this.id = id;
        this.name = name;
        this.description = description;
        this.contentType = contentType;
        this.transitionType = transitionType;
        this.orientation = orientation;
        this.html = html;
        this.updatedAt = updatedAt;
        this.archivedAt = archivedAt;
        this.uid = uid;
        this.businessName = businessName;
    }

    getUpdatedAtString() {
        return new Date(this.updatedAt * 1000).toLocaleString();
    }

    getArchivedAtString() {
        return new Date((this.archivedAt ?? 0) * 1000).toLocaleString();
    }
}

export const defaultTemplateHTML = 
`<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<link href="https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap" rel="stylesheet">
<title>Ad</title>
<style>
    html, body {
        margin: 0;
        height: 100%;
        width: 100vw;
    }
    body {
        background-color: #2B2B2B;
        color: white;
        font-family: Inter, serif;
        display: flex;
        flex-direction: column;
    }
    .text-container {
        flex: none;
        position: relative;
    }
    .media-container {
        position: relative;
        overflow-y: scroll;
        overflow: hidden;
        width: 100%;
        height: 100%;
        flex: auto;
        animation: fadeInFromLeft 1.5s ease;
    }
    .title {
        display: flex;
        align-items: center;
        padding: 7px;
        padding-left: 10px;
    }
    .headline {
        background-color: #1B1B1B;
        padding: 10px;
    }
    .slide {
        position: absolute;
        height: 100%;
        width: 100%;
        opacity: 0;
        transition: opacity 1s ease-in-out;
    }
    .slide.active {
        opacity: 1;
    }
    .ad-marker {
        background-color: #E20033;
        padding: 3px;
        margin-right: 10px;
    }
    .ad-marker-hover {
        position: absolute;
        top: 15px;
        left: 15px;
        margin: 0;
        z-index: 1;
    }
    .line {
        height: 1px;
        background: repeating-linear-gradient(90deg, #E20033 0 4px,#0000 0 8px);
    }
    .cover {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
    h4, h5 {
        margin: 0;
    }
</style>
</head>
<body>
    <div class="text-container" id="title-container"></div>
    <div class="media-container" id="media-container"></div>
    <div class="text-container" id="headline-container"></div>
  
    <script id="title-script">
        (function() {
            const lineElement = document.createElement('div');
            lineElement.className = "line";
    
            // Create Title Component
            const titleContainer = document.getElementById('title-container');
            titleContainer.innerHTML = '';
            const title = "{{title}}";
            if (!title.startsWith('{{') && title !== "") {
                // If title is provided, create header 
                var titleDiv = document.createElement('div');
                titleDiv.className = "title";
                var titleElement = document.createElement('h4');
                titleElement.textContent = title;
                var adMarkerElement = document.createElement('h4');
                adMarkerElement.className = "ad-marker";
                adMarkerElement.textContent = "AD";
                titleDiv.appendChild(adMarkerElement);
                titleDiv.appendChild(titleElement);
                titleContainer.appendChild(lineElement.cloneNode(true));
                titleContainer.appendChild(titleDiv);
                titleContainer.appendChild(lineElement.cloneNode(true));
            } else {
                // If no title is provided, create AD marker overlay
                var adMarkerElement = document.createElement('h3');
                adMarkerElement.className = "ad-marker ad-marker-hover";
                adMarkerElement.textContent = "AD";
                titleContainer.appendChild(lineElement.cloneNode(true));
                titleContainer.appendChild(adMarkerElement);
            }
        })();
    </script>
    <script id="headline-script">
        (function() {
            const lineElement = document.createElement('div');
            lineElement.className = "line";

            // Create Headline component
            const headline = "{{headline}}";
            const headlineContainer = document.getElementById('headline-container');
            headlineContainer.innerHTML = '';
            if (!headline.startsWith('{{') && headline !== "") {
                var headlineElement = document.createElement('h5');
                headlineElement.className = "headline";
                headlineElement.textContent = headline;
                headlineContainer.appendChild(lineElement.cloneNode(true));
                headlineContainer.appendChild(headlineElement);
            }
        })();
    </script>
    <script id="media-script">
        (function() {
            // Create Media component
            const mediaLinks = ["{{media1}}", "{{media2}}", "{{media3}}"];
            // For previewing template on dashboard only
            const mediaTypes = ["{{mediaType1}}", "{{mediaType2}}", "{{mediaType3}}"];
            const container = document.getElementById('media-container');
            container.innerHTML = '';

            mediaLinks.forEach((media, index) => {
                // Skip if the media is a placeholder
                if (media.startsWith('{{') && media.endsWith('}}')) {
                    return;
                }
    
                // Create slide element
                const slide = document.createElement('div');
                slide.className = 'slide';
    
                // Set the first slide as active
                if (index === 0) {
                    slide.classList.add('active');
                }
    
                // Check the media type and create the corresponding element
                if (mediaTypes[index] === 'video' || media.endsWith('.mp4') || media.endsWith('.m3u8')) {
                    const video = document.createElement('video');
                    video.className = "cover";
                    video.setAttribute('playsinline', '');
                    video.setAttribute('loop', '');
                    video.setAttribute('muted', '');

                    video.src = media;
                    video.addEventListener('loadedmetadata', function() {
                        if (index == 0) {
                            video.play();
                        }
                    });
    
                    slide.appendChild(video);
                } else {
                    const img = document.createElement('img');
                    img.setAttribute('src', media);
                    img.setAttribute('alt', \`Slide \${index + 1}\`);
                    img.className = "cover";
                    slide.appendChild(img);
                }
                
                container.appendChild(slide);
            });
        })();
    </script>
    <script id="slides-script">
        (function() {
            const slides = document.querySelectorAll('.slide');
            let currentIndex = 0;
    
            if (slides.length > 1) {
                function switchSlides() {
                    // Pause current video
                    const oldVideoElement = slides[currentIndex].querySelector('video');
                    if (oldVideoElement) {
                        oldVideoElement.pause();
                    }
                    // Remove active class from current slide
                    slides[currentIndex].classList.remove('active');
    
                    // Move to the next slide
                    currentIndex = (currentIndex + 1) % slides.length;
    
                    // Add active class to the next slide
                    slides[currentIndex].classList.add('active');
                    
                    const videoElement = slides[currentIndex].querySelector('video');
                    if (videoElement) {
                        videoElement.currentTime = 0; // Reset video to the beginning
                        videoElement.play(); // Play the video
                    }
                }
    
                // Switch slides every 3 seconds
                setInterval(switchSlides, 3000);
            }
        })();
    </script>
</body>
</html>`;
